import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetSubscriptionTermResult } from '@whinerp/client';
import { Button, Num, Panel, SectionLabel, Skeleton, ErrorState } from '@whinerp/ui';
import { formatCount, formatDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';

/**
 * The term timeline and renewal countdown (F-051).
 *
 * SC-44's own specification has named both since it was written, and the
 * subscription carried a start date and nothing else about time - so neither
 * could be drawn.
 *
 * **Open-ended is said, not counted.** A month-to-month arrangement renews by
 * not stopping, and a countdown showing zero would read as ending today.
 *
 * **A date that has passed is a real state**, because the sweep only runs when
 * somebody opens this screen and nobody may have. It says how long ago rather
 * than showing a negative number.
 *
 * **Every term is drawn, not just the current one.** Rolling forward
 * overwrites the end date, and "how long have they been with us" is a question
 * one overwritten column cannot answer.
 */
export function TermPanel({ subscriptionId }: { subscriptionId: string }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [endsOn, setEndsOn] = useState('');
  const [months, setMonths] = useState('12');
  const [autoRenews, setAutoRenews] = useState(false);

  const term = useQuery<GetSubscriptionTermResult, ApiError>({
    queryKey: ['subscription-term', subscriptionId],
    queryFn: () => api.getSubscriptionTerm({ path: { subscriptionId } }),
  });

  const save = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.setSubscriptionTerm({
        path: { subscriptionId },
        body: {
          endsOn: endsOn.trim() === '' ? null : endsOn.trim(),
          termMonths: autoRenews ? Number(months) : null,
          autoRenews,
        },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscription-term', subscriptionId] }),
  });

  if (term.isPending) return <Skeleton className="h-block-sm w-full" />;
  /*
   * A failure is not a loading state.
   *
   * These returned a Skeleton, so a panel whose request failed showed a
   * shimmer for ever - a screen that never loads rather than one that says
   * what went wrong, and a `settled()` that never settles (D-365).
   */
  if (term.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={term.error.message}
        onRetry={() => void term.refetch()}
      />
    );
  }

  const data = term.data;
  const days = data.daysToRenewal;

  return (
    <Panel data-region="term">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('term.title')}</SectionLabel>

        {save.error ? (
          <p data-term-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {save.error.message}
          </p>
        ) : null}

        {/* The countdown, or the sentence that replaces it. */}
        {data.endedAt !== null ? (
          <p data-term-ended className="text-meta text-ink-meta">
            {t('term.ended')}
          </p>
        ) : data.endsOn === null ? (
          <p data-term-open className="text-meta text-ink-meta">
            {t('term.openEnded')}
          </p>
        ) : days !== null && days < 0 ? (
          <p data-term-overdue className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text">
            {t('term.overdue', { values: { days: formatCount(Math.abs(days), format) } })}
          </p>
        ) : (
          <p data-term-countdown className="text-body text-ink">
            <Num>
              {data.autoRenews
                ? t('term.countdown', { values: { days: formatCount(days ?? 0, format) } })
                : t('term.countdown.ends', { values: { days: formatCount(days ?? 0, format) } })}
            </Num>
          </p>
        )}

        <p data-term-renews className="text-meta text-ink-meta">
          {data.autoRenews ? t('term.autoRenews') : t('term.manual')}
        </p>

        {/* Every term it has run. */}
        <SectionLabel>{t('term.history')}</SectionLabel>
        <ul className="flex flex-col">
          {data.history.map((entry) => (
            <li
              key={`${entry.startedOn}-${entry.kind}`}
              data-term-entry={entry.startedOn}
              className="flex flex-wrap items-baseline gap-2 border-t border-t-rule py-1 first:border-t-0"
            >
              <span className="text-meta text-ink-meta">
                {entry.kind === 'first' ? t('term.history.first') : t('term.history.renewal')}
              </span>
              <span className="text-meta text-ink">
                {t('term.from', { values: { start: formatDate(entry.startedOn, format) } })}{' '}
                {entry.endsOn === null
                  ? ''
                  : t('term.until', { values: { end: formatDate(entry.endsOn, format) } })}
              </span>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-end gap-2 border-t border-t-rule pt-3">
          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('term.endsOn')}
            <input
              data-term-ends-on
              type="date"
              className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={endsOn}
              onChange={(event) => setEndsOn(event.target.value)}
            />
          </label>
          <label className="inline-flex items-center gap-1 text-meta text-ink">
            <input
              type="checkbox"
              data-term-auto
              checked={autoRenews}
              onChange={(event) => setAutoRenews(event.target.checked)}
            />
            {t('term.autoRenews')}
          </label>
          {autoRenews ? (
            <label className="flex flex-col gap-1 text-dense font-medium text-ink">
              {t('term.months')}
              <input
                data-term-months
                inputMode="numeric"
                className="w-col-count rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={months}
                onChange={(event) => setMonths(event.target.value)}
              />
            </label>
          ) : null}
          <Button intent="secondary" size="small" data-term-save onClick={() => save.mutate()}>
            {t('term.save')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
